// Clock and Coins generator. Alternates clock reading (then elapsed time) with US money
// (totals, then making change). Times are "h:mm"; money is "64¢" or "$1.35".

import { int, pick, shuffle, type Rng } from '@kit/rng';

export type CoinKind = 'p' | 'n' | 'd' | 'q' | 'h' | 'b';   // penny nickel dime quarter half-dollar one-dollar bill
export const COIN_VALUE: Record<CoinKind, number> = { p: 1, n: 5, d: 10, q: 25, h: 50, b: 100 };
export const COIN_NAME: Record<CoinKind, string> = { p: 'penny', n: 'nickel', d: 'dime', q: 'quarter', h: 'half dollar', b: 'dollar' };

export type CCProblem =
  | { kind: 'clock'; h: number; m: number; question: string; answer: string; choices: string[]; hint: string }
  | { kind: 'elapsed'; h: number; m: number; add: number; question: string; answer: string; choices: string[]; hint: string }
  | { kind: 'coins'; coins: CoinKind[]; question: string; answer: string; choices: string[]; hint: string; cents: number }
  | { kind: 'change'; cost: number; paid: number; question: string; answer: string; choices: string[]; hint: string; cents: number };

export function fmtTime(h: number, m: number): string {
  const hh = ((h - 1) % 12 + 12) % 12 + 1;
  return `${hh}:${String(m).padStart(2, '0')}`;
}

export function fmtMoney(cents: number): string {
  if (cents < 100) return `${cents}¢`;
  return `$${(cents / 100).toFixed(2)}`;
}

function addMinutes(h: number, m: number, add: number): [number, number] {
  const total = ((h % 12) * 60 + m + add) % 720;
  const nh = Math.floor(total / 60) || 12;
  return [nh === 0 ? 12 : nh, total % 60];
}

function timeChoices(rng: Rng, h: number, m: number, step: number): string[] {
  const answer = fmtTime(h, m);
  const set = new Set<string>([answer]);
  const cands: [number, number][] = [
    addMinutes(h, m, step), addMinutes(h, m, -step), addMinutes(h, m, 60), addMinutes(h, m, -60),
    addMinutes(h, m, 30), addMinutes(h, m, -30), addMinutes(h, m, 2 * step), addMinutes(h, m, -2 * step),
  ];
  // the classic hour-hand mix-up: read the hour hand as the next hour, or the minute hand as the hour
  if (m >= 30) cands.unshift(addMinutes(h, m, 60));
  cands.push([Math.max(1, Math.round(m / 5)) || 12, (h % 12) * 5]);
  for (const [ch, cm] of shuffle(rng, cands)) { if (set.size >= 4) break; set.add(fmtTime(ch, cm)); }
  let guard = 0;
  while (set.size < 4 && guard++ < 50) set.add(fmtTime(int(rng, 1, 12), int(rng, 0, 11) * 5));
  return shuffle(rng, [...set]);
}

function moneyChoices(rng: Rng, cents: number): string[] {
  const set = new Set<number>([cents]);
  for (const d of shuffle(rng, [1, -1, 5, -5, 10, -10, 25, -25, 100, -100, 4, -4, 20, -20])) { if (set.size >= 4) break; const v = cents + d; if (v > 0 && !set.has(v)) set.add(v); }
  let guard = 0;
  while (set.size < 4 && guard++ < 50) { const v = cents + int(rng, -30, 30); if (v > 0) set.add(v); }
  return shuffle(rng, [...set]).map(fmtMoney);
}

export function makeClockCoins(level: number, rng: Rng, index: number): CCProblem {
  const L = Math.max(1, Math.min(10, level));
  const clockTurn = index % 2 === 0;
  if (clockTurn) {
    if (L >= 7) {
      const m = L >= 9 ? int(rng, 0, 59) : int(rng, 0, 11) * 5;
      const h = int(rng, 1, 12);
      const add = L >= 9 ? pick(rng, [15, 25, 35, 40, 45, 50, 55, 65, 75, 95]) : pick(rng, [10, 15, 20, 25, 30, 35, 40, 45]);
      const [nh, nm] = addMinutes(h, m, add);
      const toHour = (60 - m) % 60;
      return { kind: 'elapsed', h, m, add, question: `It is ${fmtTime(h, m)}. What time is it ${add} minutes later?`, answer: fmtTime(nh, nm), choices: timeChoices(rng, nh, nm, 5), hint: toHour && toHour < add ? `${toHour} minutes gets you to ${fmtTime(h + 1, 0)}. Then add the other ${add - toHour}.` : `Add ${add} minutes to the ${m} minutes you already have.` };
    }
    const h = int(rng, 1, 12);
    const m = L <= 2 ? pick(rng, [0, 30]) : L <= 4 ? int(rng, 0, 11) * 5 : int(rng, 0, 59);
    const step = L <= 2 ? 30 : L <= 4 ? 5 : 1;
    const hint = m === 0 ? `The long hand points straight up, so it is exactly ${h} o'clock.` : m === 30 ? `The long hand points straight down: half past. The short hand is between ${h} and ${h % 12 + 1}.` : `The short hand tells the hour (it is past ${h}). The long hand counts minutes by fives: ${m} minutes.`;
    return { kind: 'clock', h, m, question: 'What time is it?', answer: fmtTime(h, m), choices: timeChoices(rng, h, m, step), hint };
  }
  // money
  if (L <= 4) {
    const pool: CoinKind[] = L <= 2 ? ['p', 'n', 'd'] : ['p', 'n', 'd', 'q'];
    const max = L <= 2 ? 24 : 99;
    let coins: CoinKind[] = [];
    let cents = 0;
    const n = L <= 2 ? int(rng, 2, 5) : int(rng, 3, 7);
    for (let i = 0; i < n; i++) {
      const c = pick(rng, pool);
      if (cents + COIN_VALUE[c] > max) continue;
      coins.push(c); cents += COIN_VALUE[c];
    }
    if (!coins.length) { coins = ['n', 'p']; cents = 6; }
    coins.sort((a, b) => COIN_VALUE[b] - COIN_VALUE[a]);
    return { kind: 'coins', coins, cents, question: 'How much money is this?', answer: fmtMoney(cents), choices: moneyChoices(rng, cents), hint: `Start with the biggest coin and count on: ${coins.map(c => COIN_VALUE[c]).reduce<number[]>((acc, v) => [...acc, (acc[acc.length - 1] || 0) + v], []).join(', ')}.` };
  }
  if (L <= 6 || rng() < 0.35) {
    const cost = L <= 6 ? int(rng, 11, 99) : int(rng, 105, 495);
    const paid = L <= 6 ? 100 : 500;
    const change = paid - cost;
    return { kind: 'change', cost, paid, cents: change, question: `It costs ${fmtMoney(cost)}. You pay ${fmtMoney(paid)}. How much change?`, answer: fmtMoney(change), choices: moneyChoices(rng, change), hint: `Count up from ${fmtMoney(cost)} to the next round number, then up to ${fmtMoney(paid)}.` };
  }
  // dollars and cents totals with bills and coins
  const pool: CoinKind[] = ['b', 'q', 'd', 'n', 'p'];
  const coins: CoinKind[] = [];
  let cents = 0;
  const bills = int(rng, 1, L >= 9 ? 4 : 2);
  for (let i = 0; i < bills; i++) { coins.push('b'); cents += 100; }
  const n = int(rng, 2, 6);
  for (let i = 0; i < n; i++) { const c = pick(rng, pool.slice(1)); coins.push(c); cents += COIN_VALUE[c]; }
  coins.sort((a, b) => COIN_VALUE[b] - COIN_VALUE[a]);
  return { kind: 'coins', coins, cents, question: 'How much money is this?', answer: fmtMoney(cents), choices: moneyChoices(rng, cents), hint: `${bills} dollar${bills > 1 ? 's' : ''} first, then count the coins from biggest to smallest.` };
}
